import { combineReducers, createNextState, createReducer } from '@reduxjs/toolkit';

import type { FitmentFields } from 'Core/actions/fitmentSearch/fields';
import {
  setFitmentFields,
  setFitmentSearchResponse,
  setVerifyFitmentFacets,
} from 'Core/actions/fitmentSearch/index';
import { setResponse } from 'Core/actions/response';
import { FacetCollection, FacetValue, type FacetValueFull } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import type { VehicleValue } from 'Models/vehicle';
import getVehicleFromUri from 'Modules/converter/getVehicleFromUri';
import type { SearchErrorInfo } from 'Modules/serverApi/types';
import { equalByMany } from 'Utils/array';
import { loadSelectedVehicle } from 'Utils/userStorage';

export function getInitialVehicleSelection(): VehicleValue[] {
  const vehicleFromUriSelection = getVehicleFromUri().selection;
  return vehicleFromUriSelection.length ? vehicleFromUriSelection : loadSelectedVehicle().selection;
}

export function updateVehicleSelection(
  selection: VehicleValue[],
  selectedValue: VehicleValue,
  fitmentFields: string[],
): VehicleValue[] {
  const updatedSelection = [...selection];

  const valueIndexToReplace = updatedSelection.findIndex((value) => value.field === selectedValue.field);

  if (valueIndexToReplace !== -1) {
    updatedSelection[valueIndexToReplace] = selectedValue;
  } else {
    const valueIndexToInsert = fitmentFields.indexOf(selectedValue.field);

    updatedSelection.splice(valueIndexToInsert, 0, selectedValue);
  }

  return updatedSelection;
}

type FitmentResponseState = {
  error: SearchErrorInfo | null;
  facets: FacetCollection;
  totalHits: number;
};

export const fitmentResponseInitState: FitmentResponseState = {
  error: null,
  facets: new FacetCollection(),
  totalHits: -1,
};

export const fitmentResponseReducer = combineReducers({
  error: createReducer(fitmentResponseInitState.error, (builder) =>
    builder.addCase(setResponse, (_state, { payload }) => payload._clientError ?? null),
  ),
  facets: createReducer(fitmentResponseInitState.facets, (builder) =>
    builder
      .addCase(setResponse, (_state, { payload: { Facets } }) => new FacetCollection(Facets))
      .addCase(setFitmentSearchResponse, (_state, { payload }) => new FacetCollection(payload))
      .addCase(
        setVerifyFitmentFacets,
        (
          state,
          {
            payload,
            meta: { isAutoVehicleSelectionDisabled, onInit, vehicleJustDiscarded, vehicleJustSelected },
          },
        ) =>
          onInit ||
          vehicleJustDiscarded ||
          vehicleJustSelected ||
          (payload.filter((f) => !f.selection.length).length === 0 && !isAutoVehicleSelectionDisabled)
            ? payload
            : state,
      ),
  ),
  totalHits: createReducer(fitmentResponseInitState.totalHits, (builder) =>
    builder.addCase(setResponse, (_state, { payload }) => payload.TotalHits),
  ),
});

const fieldsInitState: FitmentFields = {
  base: fitmentSearchConfig.baseFields,
  extra: fitmentSearchConfig.extraFields,
};

export const fitmentFieldsReducer = createReducer(fieldsInitState, (builder) =>
  builder.addCase(setFitmentFields, (state, { payload }) => payload || state),
);

export interface FitmentFixableState {
  request: { selection: VehicleValue[] };
  response: { facets: { selection: FacetValueFull[] } };
}

export function fixRequestDueToResponse<S extends FitmentFixableState>(
  state: S | undefined,
  fix: boolean,
): S | undefined {
  if (!state || !fix) {
    return state;
  }
  return createNextState(state, (draft) => {
    const { selection } = draft.request;
    const responseSelection: VehicleValue[] = draft.response.facets.selection.filter((v) =>
      fitmentSearchConfig.fields.includes(v.field),
    );

    if (!equalByMany(selection, responseSelection, FacetValue.termKey, 'isUnique')) {
      draft.request.selection = responseSelection;
    }
  });
}
